import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, RefreshCw, X, Check, SwitchCamera, Loader2 } from "lucide-react";

export const isInAppBrowser = () => {
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|WhatsApp|Line\/|Twitter|; wv\)|MicroMessenger/i.test(ua);
};

export const canUseLiveCamera = () =>
  typeof window !== "undefined" && window.isSecureContext && !!navigator.mediaDevices?.getUserMedia;

const ERROS = {
  NotAllowedError: "Permissão da câmera negada. Toque no ícone de cadeado/câmera na barra do navegador e permita o acesso, .",
  NotFoundError: "Nenhuma câmera encontrada neste dispositivo.",
  NotReadableError: "A câmera está em uso por outro aplicativo. Feche-o e tente novamente.",
  OverconstrainedError: "Câmera solicitada indisponível. Tentando outra câmera...",
};

export function CameraCapture({ open, facing = "environment", title, onCapture, onClose, onFallback }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [face, setFace] = useState(facing);
  const [erro, setErro] = useState("");
  const [ready, setReady] = useState(false);
  const [shot, setShot] = useState(null);

  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };

  useEffect(() => {
    if (!open) { stop(); setShot(null); setErro(""); setReady(false); return undefined; }
    let cancelled = false;
    const start = async (mode, retry = true) => {
      setErro(""); setReady(false);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
        setReady(true);
      } catch (e) {
        if (retry && e?.name === "OverconstrainedError") return start(undefined, false);
        setErro(ERROS[e?.name] || `Não foi possível abrir a câmera (${e?.name || "erro"}).`);
      }
    };
    start(face);
    return () => { cancelled = true; stop(); };
  }, [open, face]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    if (face === "user") { ctx.translate(c.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(v, 0, 0);
    c.toBlob((blob) => blob && setShot({ blob, url: URL.createObjectURL(blob) }), "image/jpeg", 0.9);
  };

  const confirm = () => {
    if (!shot) return;
    onCapture(new File([shot.blob], `foto-${Date.now()}.jpg`, { type: "image/jpeg" }));
    URL.revokeObjectURL(shot.url);
    onClose();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="camera-modal" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="font-semibold text-sm truncate">{title || "Tirar foto"}</div>
        <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label="Fechar" data-testid="camera-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {shot ? (
          <img src={shot.url} alt="Foto capturada" className="max-h-full max-w-full object-contain" data-testid="camera-preview" />
        ) : (
          <video ref={videoRef} playsInline muted autoPlay className={`max-h-full max-w-full object-contain ${face === "user" ? "-scale-x-100" : ""}`} data-testid="camera-video" />
        )}
        {!ready && !erro && !shot && <Loader2 className="absolute w-8 h-8 text-white animate-spin" />}
        {erro && (
          <div className="absolute inset-x-4 top-4 bg-white/95 text-slate-800 rounded-xl p-4 text-sm space-y-3" data-testid="camera-error">
            <p>{erro}</p>
            <div className="flex gap-2 flex-wrap">
              <Button type="button" size="sm" variant="outline" onClick={() => setFace((f) => f)} data-testid="camera-retry"><RefreshCw className="w-4 h-4 mr-1" /> Tentar de novo</Button>
              {onFallback ? <Button type="button" size="sm" className="bg-gold text-black hover:bg-gold/90" onClick={() => { onClose(); onFallback(); }} data-testid="camera-fallback">Escolher da galeria</Button>
                : <span className="text-xs text-amber-200 self-center" data-testid="camera-so-atual">Esta foto exige captura atual pela câmera; a galeria não é permitida. Permita o acesso à câmera para continuar.</span>}
            </div>
          </div>
        )}
      </div>
      <div className="px-6 py-5 flex items-center justify-center gap-6 text-white">
        {shot ? (
          <>
            <Button type="button" variant="outline" className="bg-transparent text-white border-white/40 h-12 px-5" onClick={() => setShot(null)} data-testid="camera-retake"><RefreshCw className="w-5 h-5 mr-2" /> Refazer</Button>
            <Button type="button" className="bg-gold text-black hover:bg-gold/90 h-12 px-6 font-bold" onClick={confirm} data-testid="camera-confirm"><Check className="w-5 h-5 mr-2" /> Usar foto</Button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setFace((f) => (f === "user" ? "environment" : "user"))} className="p-3 rounded-full bg-white/10" aria-label="Trocar câmera" data-testid="camera-switch"><SwitchCamera className="w-6 h-6" /></button>
            <button type="button" onClick={capture} disabled={!ready} className="w-16 h-16 rounded-full bg-white border-4 border-gold disabled:opacity-40 flex items-center justify-center" aria-label="Capturar" data-testid="camera-shutter"><Camera className="w-7 h-7 text-black" /></button>
            <div className="w-12" />
          </>
        )}
      </div>
    </div>
  );
}
